// HUDPanel — the floating content card. Anchored to one of three sides
// (left / right / center) and one of three modes (open / closed). When
// open, it fades in with a small upward translateY. When closed it fades
// out to opacity 0.
//
// Pure presentational component — driven entirely by props (which scene
// is showing what kind of content). The parent <HUDController /> owns
// the panel-open / close state and which scene is active.
//
// The close button is wrapped in <Magnetic /> so it gets the
// cursor-magnet + hover-scale + click-squish treatment. An optional
// "Read more" link can be rendered as a Magnetic anchor that opens
// the deep-dive DetailPanel for the active scene.
//
// An optional copy-link button (a small chain-link icon) sits next to
// the close button when `copyLink` is a non-null URL. On click it
// writes the URL to the clipboard and flashes a checkmark for ~900ms
// before reverting to the chain icon.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Magnetic } from './Magnetic';
import { openDetail } from './SatelliteInteraction';

export type HUDPanelSide = 'left' | 'right' | 'center';

/** Optional hook for opening the deep-dive panel. */
export interface HUDPanelDeepDive {
  /** Detail panel type. */
  type: 'project' | 'note' | 'milestone' | 'about' | 'skills' | 'contact';
  /** Detail panel id. */
  id: string;
  /** Visible label of the trigger. */
  label?: string;
}

export interface HUDPanelProps {
  /** Side of the viewport the card anchors to. */
  side?: HUDPanelSide;
  /** Whether the panel is visible. */
  open: boolean;
  /** Called when the user clicks the close button or hits Esc. */
  onClose: () => void;
  /** Children render inside the card body. */
  children: ReactNode;
  /** Extra className for callers that need to theme-vary the card. */
  className?: string;
  /** Hide the close button (e.g. when the panel is "always on"). */
  hideClose?: boolean;
  /** ARIA label for the panel root. */
  ariaLabel?: string;
  /** Optional "Read more" trigger — opens the deep-dive detail panel. */
  readMore?: HUDPanelDeepDive;
  /** Shareable URL to copy when the user clicks the chain icon. When
   *  omitted (or null) the copy button is hidden entirely. */
  copyLink?: string | null;
}

/** Inline chain-link SVG. 16px visual line; uses currentColor so it
 *  picks up the button's text-secondary / accent hover state. */
function ChainIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

/** Inline check SVG — shown briefly after a successful copy. */
function CheckIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export function HUDPanel({
  side = 'left',
  open,
  onClose,
  children,
  className,
  hideClose = false,
  ariaLabel,
  readMore,
  copyLink = null,
}: HUDPanelProps) {
  const sideClass =
    side === 'left'
      ? 'hud-panel--left'
      : side === 'right'
        ? 'hud-panel--right'
        : 'hud-panel--center';
  const composedClassName = [
    'hud-panel',
    sideClass,
    open ? 'is-open' : 'is-closed',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<number | null>(null);

  // Reset the "copied" flash if the panel is unmounted while it's
  // visible, or if the URL changes under the user's feet.
  useEffect(() => {
    if (!copied) return;
    setCopied(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyLink]);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current !== null) {
        window.clearTimeout(copyTimerRef.current);
      }
    };
  }, []);

  const handleCopy = async () => {
    if (!copyLink) return;
    let ok = false;
    try {
      if (
        typeof navigator !== 'undefined' &&
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === 'function'
      ) {
        await navigator.clipboard.writeText(copyLink);
        ok = true;
      } else {
        // Fallback for older browsers — temporary textarea + execCommand.
        const ta = document.createElement('textarea');
        ta.value = copyLink;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        ta.style.pointerEvents = 'none';
        document.body.appendChild(ta);
        ta.select();
        try {
          ok = document.execCommand('copy');
        } catch {
          ok = false;
        }
        document.body.removeChild(ta);
      }
    } catch {
      ok = false;
    }
    if (ok) {
      setCopied(true);
      if (copyTimerRef.current !== null) {
        window.clearTimeout(copyTimerRef.current);
      }
      // 900ms flash — long enough to read, short enough not to nag.
      copyTimerRef.current = window.setTimeout(() => {
        setCopied(false);
        copyTimerRef.current = null;
      }, 900);
    }
  };

  const showCopy = !!copyLink;

  return (
    <aside
      className={composedClassName}
      role="complementary"
      aria-label={ariaLabel}
      aria-hidden={!open}
    >
      {showCopy && (
        <Magnetic
          as="button"
          className={[
            'hud-panel__copy',
            copied ? 'is-copied' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          ariaLabel="Copy scene link"
          title="Copy link to this scene"
          onClick={handleCopy}
          type="button"
          innerClassName="hud-panel__copy-inner"
        >
          {copied ? <CheckIcon /> : <ChainIcon />}
        </Magnetic>
      )}
      {!hideClose && (
        <Magnetic
          as="button"
          className="hud-panel__close"
          ariaLabel="Close panel"
          onClick={onClose}
          type="button"
          innerClassName="hud-panel__close-inner"
        >
          <span aria-hidden="true">×</span>
        </Magnetic>
      )}
      {children}
      {readMore && (
        <div className="hud-panel__readmore-row">
          <Magnetic
            as="button"
            className="hud-panel__readmore"
            ariaLabel={readMore.label ?? 'Read more'}
            type="button"
            onClick={() => openDetail({ type: readMore.type, id: readMore.id })}
            innerClassName="hud-panel__readmore-inner"
          >
            <span>{readMore.label ?? 'Read more'}</span>
            <span aria-hidden="true" className="hud-panel__readmore-arrow">→</span>
          </Magnetic>
        </div>
      )}
    </aside>
  );
}